import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Checkbox,
  Divider,
  Stack,
} from "@mui/material";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import { generateDocument, getInvoice } from "../../Orders/Invoice/Invoice";
import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const EmailInvoices = (props) => {
  const classes = useStyles();

  const cDate = new Date();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(cDate);
  const [invoices, setInvoices] = useState([]);

  const handleChange = (e) => {
    const { name, checked, value } = e.target;

    const [, invoiceId, fName, ccIndex] = name.split("-");

    setInvoices((prevState) =>
      prevState.map((item) => {
        if (item.order_id !== +invoiceId) return item;

        if (["custom_subject", "note"].includes(fName)) {
          item[fName] = value;
        } else if (name.includes("confirm")) {
          if (name.includes("cc")) {
            item.cc_addresses_confirm[ccIndex] = checked;
          } else {
            item.main_address_confirm = checked;
          }
        } else {
          if (name.includes("cc")) {
            item.company_location_cc_addresses[ccIndex] = value;
          } else {
            item.customer.email = value;
          }
        }

        return item;
      }),
    );
  };

  const handleChangeDate = (e) => {
    const { value } = e.target;

    setInvoices([]);
    setDeliveryDate(value);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to share the invoice to the selected emails?
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Sending Invoice emails <br />
              This may take some time relative to the number of invoices and
              emails
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while sending the emails
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {errorInfo.message}
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The selected invoices/emails were sent successfully.
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while generating pdf for the following invoices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {errorInfo.message}
              </span>
            </>
          ) : (
            <></>
          )}
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    const invoicesToShare = invoices.filter((item) => {
      if (item.main_address_confirm && item.customer.email !== "") return true;

      if (item.cc_addresses_confirm !== null) {
        return item.cc_addresses_confirm.some((cItem) => cItem);
      }

      return false;
    });

    let errorMsg = "";

    // todo: create a getInvoice to make sure we totalize the subitem weight lbs and lot code counts
    Promise.all(
      invoicesToShare.map(
        async (iItem) =>
          await getInvoice(iItem.order_id).then(async (data) => {
            const toEmail = iItem.main_address_confirm
              ? iItem.customer.email
              : "";

            if (toEmail !== "") {
              try {
                const pdfFile = generateDocument(data);

                let invoiceFormData = new FormData();

                invoiceFormData.append("file", pdfFile);
                invoiceFormData.append("to_email", toEmail);

                if (iItem.cc_addresses_confirm !== null) {
                  iItem.cc_addresses_confirm.forEach((item, index) => {
                    if (item) {
                      invoiceFormData.append(
                        `additional_emails[${index}]`,
                        iItem.company_location_cc_addresses[index],
                      );
                    }
                  });
                }

                if (iItem.custom_subject !== "") {
                  invoiceFormData.append(
                    "custom_subject",
                    iItem.custom_subject,
                  );
                }

                if (iItem.note !== "") {
                  invoiceFormData.append("note", iItem.note);
                }

                await backend.post(
                  `${API_URL}orders/${iItem.order_id}/invoice/email`,
                  invoiceFormData,
                  {
                    headers: { "Content-Type": "multipart/form-data" },
                  },
                );

                return invoiceFormData;
              } catch (error) {
                console.log(error);
                errorMsg +=
                  "\nThere was an error processing Invoice: " + iItem.order_id;

                return error;
              }
            }
          }),
      ),
    ).then(() => {
      if (errorMsg) {
        setErrorInfo({ error: true, message: errorMsg });
      }

      setLoadingFlag(false);
      setFinishedFlag(true);
    });
  };

  useEffect(() => {
    const getInvoices = async () => {
      let deliveryDateObj = new Date(deliveryDate);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      await backend
        .get(
          `${API_URL}invoices?no_page=1&start_date=${deliveryDateParam}&end_date=${deliveryDateParam}`,
        )
        .then(({ data }) => {
          if (data.length) {
            const dataMap = data.map((item) => {
              const mainMailValid = item.customer && item.customer.email !== "";

              const ccAddressesMap =
                item.company_location_cc_addresses === null
                  ? null
                  : item.company_location_cc_addresses.map(() =>
                      mainMailValid ? true : false,
                    );

              const orderDateObj = new Date(item.order_delivery_date);

              const deliveryDateString = new Date(
                orderDateObj.getTime() -
                  orderDateObj.getTimezoneOffset() * 60000,
              )
                .toISOString()
                .split("T")[0];

              return {
                ...item,
                cc_addresses_confirm: ccAddressesMap,
                main_address_confirm: mainMailValid,
                order_delivery_date_string: deliveryDateString,
                custom_subject: "",
                note: "",
              };
            });

            setInvoices(dataMap);
          }
        });
    };

    if (deliveryDate !== null) {
      getInvoices();
    }
  }, [deliveryDate]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Email Invoices
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Use this interface to perform a bulk email sharing from the Invoices
          of the date.
          <Typography variant="body2" fontStyle="italic">
            (This may take some time relative to the number of invoices and
            emails)
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load invoices from a given date.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "delivery_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => toggleConfirmModal()}
              className={classes.button}
              disabled={
                loadingFlag ||
                !invoices.length ||
                !invoices.some((item) => item.main_address_confirm)
              }
            >
              Bulk Invoice Share
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}></Grid>
        {invoices.length ? (
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                Invoice ID
              </Grid>
              <Grid item xs={12} sm={3}>
                Date
              </Grid>
              <Grid item xs={12} sm={3}>
                Total
              </Grid>
              <Grid item xs={12} sm={3}>
                Company Location
              </Grid>
              {invoices.map((item) => (
                <Fragment key={`invoice-${item.order_id}`}>
                  <Grid item xs={12} sm={3}>
                    <b>{item.order_id}</b>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <b>{item.order_delivery_date_string}</b>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <b>{item.grand_total}</b>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <b>{item.company_location_name}</b>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={"auto"}>
                        <Checkbox
                          name={`invoice-${item.order_id}-main-0-confirm`}
                          value={item && item.main_address_confirm}
                          checked={item && item.main_address_confirm}
                          onChange={(e) => handleChange(e)}
                          color="primary"
                        />
                      </Grid>
                      <Grid item xs={true}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          label="Main Email "
                          name={`invoice-${item.order_id}-main-0-email`}
                          value={(item.customer && item.customer.email) || ""}
                          onChange={(e) => handleChange(e)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          id="outlined-basic"
                          variant="outlined"
                          label="Custom Subject"
                          name={`invoice-${item.order_id}-custom_subject`}
                          value={item.custom_subject}
                          onChange={(e) => handleChange(e)}
                        />
                      </Grid>
                      <Grid item xs={12}></Grid>
                      {item.company_location_cc_addresses !== null ? (
                        item.company_location_cc_addresses.map(
                          (ccItem, ccIndex) => (
                            <Fragment
                              key={`invoice-${item.order_id}-cc-${ccIndex}`}
                            >
                              <Grid item xs={"auto"}>
                                <Checkbox
                                  name={`invoice-${item.order_id}-cc-${ccIndex}-confirm`}
                                  color="primary"
                                  value={
                                    item.customer &&
                                    item.customer.email === "" &&
                                    !item.main_address_confirm
                                      ? false
                                      : item.cc_addresses_confirm !== null &&
                                        item.cc_addresses_confirm[ccIndex]
                                  }
                                  checked={
                                    item.customer &&
                                    item.customer.email === "" &&
                                    !item.main_address_confirm
                                      ? false
                                      : item.cc_addresses_confirm !== null &&
                                        item.cc_addresses_confirm[ccIndex]
                                  }
                                  disabled={
                                    (item.customer &&
                                      item.customer.email === "") ||
                                    !item.main_address_confirm
                                  }
                                  onChange={(e) => handleChange(e)}
                                />
                              </Grid>
                              <Grid item xs={true}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  variant="standard"
                                  label={`CC Email ${ccIndex + 1} `}
                                  name={`invoice-${item.order_id}-cc-${ccIndex}-email`}
                                  value={
                                    (item.company_location_cc_addresses !==
                                      null &&
                                      item.company_location_cc_addresses[
                                        ccIndex
                                      ]) ||
                                    ""
                                  }
                                  disabled={
                                    (item.customer &&
                                      item.customer.email === "") ||
                                    !item.main_address_confirm
                                  }
                                  onChange={(e) => handleChange(e)}
                                />
                              </Grid>
                              <Grid item xs={12}></Grid>
                            </Fragment>
                          ),
                        )
                      ) : (
                        <></>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        id="outlined-basic"
                        variant="outlined"
                        label="Note"
                        name={`invoice-${item.order_id}-note`}
                        value={item.note}
                        onChange={(e) => handleChange(e)}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}></Grid>
                </Fragment>
              ))}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => toggleConfirmModal()}
                  className={classes.button}
                  disabled={
                    loadingFlag ||
                    !invoices.length ||
                    !invoices.some((item) => item.main_address_confirm)
                  }
                >
                  Bulk Invoice Share
                </Button>
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Container>
  );
};

export default EmailInvoices;
